import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { saveAs as importedSaveAs } from 'file-saver';

import { HttpConfigService } from '@citi-gcg-173875/citi-http';

import { MktContentStorageService } from './mkt-content-storage.service';
import { isPlatformBrowser } from '@angular/common';
import { AppConfigService } from '@citi-gcg-173875/citi-utils';
import { MktEnvironmentService } from './mkt-environment.service';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class MktContentStaticService {
  private _metadata: any;
  private readonly AEM = 'AEM';
  private readonly SOURCECODE = 'sourceCode';
  private readonly LOCALE = 'en_US';

  constructor(
    private _http: HttpClient,
    private _appConfigService: AppConfigService,
    private _httpConfigService: HttpConfigService,
    private _contentStorageService: MktContentStorageService,
    @Inject(PLATFORM_ID) private platformId: string,
    private _environmentService: MktEnvironmentService
  ) {}
  private getHeaders = (options) => (isPlatformBrowser(this.platformId) ? this._httpConfigService.getOptions(options) : {});

  public getStaticMetadata(): Observable<any> {
    const self = this;
    return this._http
      .get(
        this.buildStaticMetadataUrl(),
        this.getHeaders({})
      )
      .pipe(
        map((res) => {
          self._metadata = res ? res : {};
          return self._metadata;
        }),
        catchError((error: Response) => {
          self._metadata = {};
          return throwError(error);
        })
      );
  }

  private getSourceCode(options) {
    let sourceCode;
    if (!!options) {
      sourceCode = options[this.SOURCECODE];
      delete options[this.SOURCECODE];
    }
    return sourceCode;
  }

  public getStaticBundle(appId: string, options?: {}): Observable<any> {
    const data = this._contentStorageService.getByAppId(appId);
    if(data) return of(data);
    const sourceCode = this.getSourceCode(options);
    if (
      this._appConfigService.getContentServiceConfig().useHashForSC &&
      (this._metadata === undefined || this._metadata === null)
    ) {
      return this.getStaticMetadata().pipe(
        map(
          (_) =>
            this._http
              .get(
                this.buildStaticBundleUrl(appId, sourceCode),
                this.getHeaders(options)
              )
              .pipe(
                map((res) => this.staticBundleHandler(res, appId)),
                catchError((error: Response) => {
                  return throwError(error);
                })
              ),
          catchError((_: Response) =>
            this._http
              .get(
                this.buildStaticBundleUrl(appId),
                this.getHeaders(options)
              )
              .pipe(
                map((res) => this.staticBundleHandler(res, appId)),
                catchError((error: Response) => throwError(error))
              )
          )
        )
      );
    } else {
      return this._http
        .get(
          this.buildStaticBundleUrl(appId, sourceCode),
          this.getHeaders(options)
        )
        .pipe(
          map((res) => this.staticBundleHandler(res, appId)),
          catchError((error: Response) => {
            if (sourceCode === this.AEM) {
              return this._http
                .get(
                  this.buildStaticBundleUrl(appId),
                  this.getHeaders(options)
                )
                .pipe(
                  map((res) => this.staticBundleHandler(res, appId)),
                  catchError((error: Response) => throwError(error))
                )
            }
            return throwError(error);
          })
        );
    }
  }

  private staticBundleHandler(res, appId: string) {
    const staticdata = res?.body || res || {};
    let data = {};
    if (staticdata['content']) {
      data = staticdata['content'][appId];
    }
    this._contentStorageService.set(appId, data);
    return this._contentStorageService.getByAppId(appId);
  }

  public getPageDef(appId: string, viewId: string): Observable<any> {
    const self = this;
    return this._http.get(this.buildStaticPageDefUrl(appId, viewId), {}).pipe(
      map((res) => {
        const pageDefData = res ? res : {};
        let data = {};
        if (pageDefData['content']) {
          data = pageDefData['content'][appId];
        }
        self._contentStorageService.set(appId, data);
        return self._contentStorageService.get(appId, 'page_def', viewId);
      }),
      catchError((error: Response) => {
        return throwError(error);
      })
    );
  }

  public getStaticResource(path: string) {
    const fileName: string = path.split('/')[path.split('/').length - 1];

    // let options = new RequestOptions({ responseType: ResponseContentType.Blob });
    return this._http
      .get(this.buildStaticResourceUrl(path), {})
      .pipe(
        map((res: Response) => {
          return res.blob();
        }),
        catchError((error: Response) => {
          return throwError(error);
        })
      )
      .subscribe((blob) => {
        importedSaveAs(blob, fileName);
      });
  }

  private buildStaticMetadataUrl(): string {
    // tslint:disable-next-line: max-line-length
    const completeStaticUrl: string =
      this.getStaticBaseUrl() +
      '/staticcms/' +
      this._appConfigService.getBusinessId() +
      '/' +
      this.LOCALE +
      '/metadata/metadata.json';
    return completeStaticUrl;
  }

  private buildStaticBundleUrl(appId: string, sourceCode = 'ICMS'): string {
    // Source Code: AEM or ICMS as Default.
    const BASEURL = this.getStaticBaseUrl(sourceCode);
    let completeStaticUrl: string = `${BASEURL}/staticcms/${this._appConfigService.getBusinessId()}/${this.LOCALE}/appid/`;
    if (
      this._appConfigService.getContentServiceConfig().useHashForSC &&
      typeof this._metadata === 'object'
    ) {
      const hash: string = this._metadata[appId];
      if (hash) {
        completeStaticUrl = completeStaticUrl + hash + '/';
      }
    }
    completeStaticUrl = completeStaticUrl + appId + '.json';
    if (sourceCode === 'AEM') completeStaticUrl += `?sourceCode=${sourceCode}`;
    return completeStaticUrl;
  }

  private buildStaticPageDefUrl(appId: string, viewId: string): string {
    // tslint:disable-next-line: max-line-length
    const completeStaticUrl: string =
      this.getStaticBaseUrl() +
      '/staticcms/' +
      this._appConfigService.getBusinessId() +
      this.LOCALE +
      appId +
      '/pagedef/' +
      viewId +
      '.json';
    return completeStaticUrl;
  }

  private buildStaticResourceUrl(path: string): string {
    const completeStaticUrl: string =
      this.getStaticBaseUrl() +
      '/staticcms/' +
      this._appConfigService.getBusinessId() +
      '/' +
      path;
    return completeStaticUrl;
  }

  private getStaticBaseUrl(sourceCode = 'ICMS'): string {
    let baseUrl = '';
    if (isPlatformBrowser(this.platformId)) {
      baseUrl = this._environmentService.getEnvironmentVar(
        'apiServerUrlPlatformClient'
      );
    } else {
      // Need to use private URL for when running on the server
      baseUrl = this._environmentService.getEnvironmentVar(
        'apiServerUrlPlatformServer'
      );
    }
    if (sourceCode === 'AEM') baseUrl += '/prelogin/aem/contentorches';
    return baseUrl;
  }
}
